import { ordersHistory, createOnlineOrder, getOnlineOrderList, cancelOnlineOrder, getOnlineOrderInfo, directPrint } from "@/services/api.service";
import { processOrdersWithMergedPriority } from '@/utils/orderSorting.utils'
import orderHistoryModel from "@/models/orderHistory.model";
import OrderGoods from "@/models/order/orderGoods.model";
import OrderDelivering from "@/models/order/orderDelivering.model";
import OrderSpec from "@/models/order/orderSpec.model";
import OrderCheckout from "@/models/order/orderCheckout.model";
import commonUtils from "@/utils/common.utils";
import moment from "moment";
import { buildPrintFields } from "@/services/cloudPrint";
import { buildOrderDetailPrintData } from "@/services/cloudPrint/orderDetailTransformer";

export default {
    namespaced: true,
    state: {
        onlineOrderHistory: [],
        onlineOrderHistoryPage: 0,
        inStoreOrderHistory: [],
        inStoreOrderHistoryPage: 0,
        pageSize: 10,
        orderNumber: '',
        orderSpecs: [],
        // LINE Pay 列印欄位（建立訂單時組好，confirm 時帶出）
        linePayPrintFields: {},
        showOrderCreateToast: true,
        productNoteMaxLength: 15,
        productNotePlaceHolder: '我是單一商品的備註文字'
    },
    getters: {
        sortedOnlineOrderHistory(state) {
            // 移除相同訂單編號的訂單
            return state.onlineOrderHistory
                .filter((item, index, self) => {
                    return self.findIndex(t => t.orderNumber === item.orderNumber) === index
                })
                // 過濾已結帳訂單 orderStatus === 3
                .filter(item => item.orderStatus !== 3)
                .sort((a, b) => {
                    return new Date(b.orderDateTime) - new Date(a.orderDateTime);
                })
                .reduce((acc, cur) => {
                    if (!acc[cur.orderDate]) {
                        acc[cur.orderDate] = {}
                        acc[cur.orderDate].date = cur.orderDate
                        acc[cur.orderDate].list = [cur]
                    } else {
                        acc[cur.orderDate].list.push(cur)
                    }
                    return acc
                }, {})
        },
        sortedInStoreOrderHistory(state) {
            // 移除相同訂單編號的訂單
            return state.inStoreOrderHistory
                .filter((item, index, self) => {
                    return self.findIndex(t => t.orderNumber === item.orderNumber) === index
                })
                .sort((a, b) => {
                    return new Date(b.orderDateTime) - new Date(a.orderDateTime);
                })
                .reduce((acc, cur) => {
                    if (!acc[cur.orderDate]) {
                        acc[cur.orderDate] = {}
                        acc[cur.orderDate].date = cur.orderDate
                        acc[cur.orderDate].list = [cur]
                    } else {
                        acc[cur.orderDate].list.push(cur)
                    }
                    return acc
                }, {})
        },
        sortedInStoreOrderHistoryWithMergedPriority(state) {
            const filteredOrders = state.inStoreOrderHistory
                .filter((item, index, self) => {
                    return self.findIndex(t => t.orderNumber === item.orderNumber) === index
                })

            return processOrdersWithMergedPriority(filteredOrders)
        },
        hasMoreOnlineOrderHistory(state) {
            return state.onlineOrderHistoryPage == -1 ? false : true
        },
        hasMoreInStoreOrderHistory(state) {
            return state.inStoreOrderHistoryPage == -1 ? false : true
        },
    },
    mutations: {
        setOnlineOrderHistory(state, payload) {
            state.onlineOrderHistory = payload
        },
        setOnlineOrderHistoryPage(state, payload) {
            state.onlineOrderHistoryPage = payload
        },
        setInStoreOrderHistory(state, payload) {
            state.inStoreOrderHistory = payload
        },
        setInStoreOrderHistoryPage(state, payload) {
            state.inStoreOrderHistoryPage = payload
        },
        setOrderNumber(state, payload) {
            state.orderNumber = payload
        },
        setLinePayPrintFields(state, payload) {
            state.linePayPrintFields = payload || {}
        },
        setOrderSpecs(state, payload) {
            state.orderSpecs = payload
        },
        setShowOrderCreateToast(state, payload) {
            state.showOrderCreateToast = payload
        }
    },
    actions: {
        async fetchOnlineOrderHistory({ commit, state, dispatch }) {
            const sendData = {
                page: state.onlineOrderHistoryPage,
                page_size: 10
            }
            const res = await getOnlineOrderList(sendData)
            console.log('order history res', res)
            if (res?.data?.msg === 'Failed') {
                commit('setOnlineOrderHistoryPage', -1)
                throw res?.data?.err_msg
            }
            let ls = res?.data?.data?.online_order_info_list || []
            // 移除沒有訂單編號的訂單，伺服器問題
            ls = ls.filter(item => item.order_number && item.order_content)
            const onlineOrderHistory = []
            for (const item of ls) {
                try {
                    // 先將 item.order_content 轉為 JSON 並取出 item.order_content.combinedOrderSheetIDs
                    // combinedOrderSheetIDs 為子單的訂單編號
                    // 使用 getOnlineOrders 取得子單資訊 並加入 order_content.combined_order_sheets
                    let orderContent = JSON.parse(item.order_content) || {}
                    if (orderContent.combinedOrderSheetIDs && orderContent.combinedOrderSheetIDs.length) {
                        try {
                            const combinedOrderSheets = (await dispatch('getOnlineOrders', orderContent.combinedOrderSheetIDs)).data.data.online_order_info
                            orderContent.combined_order_sheets = combinedOrderSheets.map(child => {
                                return JSON.parse(child.order_content)
                            })
                        } catch (err) {
                            console.log('getOnlineOrders err', err)
                        }
                    }
                    item.order_content = JSON.stringify(orderContent)
                    onlineOrderHistory.push(new orderHistoryModel(item))
                } catch (err) {
                    console.error('解析 order content', err)
                }
            }
            commit('setOnlineOrderHistory', [...state.onlineOrderHistory, ...onlineOrderHistory])
            ls.length < state.pageSize ? commit('setOnlineOrderHistoryPage', -1) : commit('setOnlineOrderHistoryPage', state.onlineOrderHistoryPage + 1)
            console.log('online order history', state.onlineOrderHistory)
        },
        async fetchInStoreOrderHistory({ commit, state, getters, dispatch }) {
            if (getters.hasMoreInStoreOrderHistory) {
                const sendData = {
                    page: state.inStoreOrderHistoryPage,
                    page_size: state.pageSize
                }
                const res = await ordersHistory(sendData)
                console.log('order history res', res)
                if (res?.data?.msg === 'Failed') {
                    // 沒有更多訂單
                    commit('setInStoreOrderHistoryPage', -1)
                    throw res?.data?.err_msg
                }
                let ls = res?.data?.data?.orders_history_info?.orders_history_list || []
                // 確保 ls 是陣列，避免 filter 錯誤
                if (!Array.isArray(ls)) {
                    console.warn('orders_history_list 不是陣列:', ls)
                    ls = []
                }
                // 移除沒有訂單編號的訂單，伺服器問題
                ls = ls.filter(item => item.order_number && item.order_content)
                // const orderHistoryModelList = ls.map(item => {
                //     if (item.order_status) {
                //         覆寫 order_status 如果為 1 改為 3 , 如果為 2 改為 1
                //         item.order_status = item.order_status === 1 ? 3 : 1
                //     }
                //     return  new orderHistoryModel(item)
                // })
                const orderHistoryModelList = []
                for (const item of ls) {
                    // 先將 item.order_content 轉為 JSON 並取出 item.order_content.combinedOrderSheetIDs
                    // combinedOrderSheetIDs 為子單的訂單編號
                    // 使用 getOnlineOrders 取得子單資訊 並加入 order_content.combined_order_sheets
                    let orderContent = JSON.parse(item.order_content)
                    if (orderContent.combinedOrderSheetIDs && orderContent.combinedOrderSheetIDs.length) {
                        try {
                            const combinedOrderSheets = (await dispatch('getOnlineOrders', orderContent.combinedOrderSheetIDs)).data.data.online_order_info
                            orderContent.combined_order_sheets = combinedOrderSheets.map(child => {
                                return JSON.parse(child.order_content)
                            })
                        } catch (err) {
                            console.log('getOnlineOrders err', err)
                        }
                    }
                    if (item.order_status) {
                        // 覆寫 order_status 如果為 1 改為 3 , 如果為 2 改為 1
                        item.order_status = item.order_status === 1 ? 3 : 1
                    }
                    item.order_content = JSON.stringify(orderContent)
                    orderHistoryModelList.push(new orderHistoryModel(item))
                }
                commit('setInStoreOrderHistory', [...state.inStoreOrderHistory, ...orderHistoryModelList])
                ls.length < state.pageSize ? commit('setInStoreOrderHistoryPage', -1) : commit('setInStoreOrderHistoryPage', state.inStoreOrderHistoryPage + 1)
                console.log('in store order history', state.inStoreOrderHistory)
            }
        },
        addOrderSpecs({ commit, state }, orderSpec) {
            const orderSpecs = [...state.orderSpecs, orderSpec].filter((item, index, self) => {
                return self.findIndex(t => t.subSpecID === item.subSpecID) === index
            })
            commit('setOrderSpecs', orderSpecs);
        },
        async createOnlineOrder({ rootState, rootGetters, commit, state }) {
            const orderGoods = rootState.cart.cart.map(item => {
                return new OrderGoods({
                    orderGood: item,
                    vipLevel: rootGetters['userData/vipLevel']
                })
            })

            const serverAreaData = {
                // 用戶桌號資料
                userTableData: rootState.cart.tableData,
                dineInTable: rootGetters['spreadsheet/dineInTables'].find(el => el.areaId === rootState.cart.tableData.areaId)
            }

            const takeOutTableData = rootState.cart.takeOutTableData || {}
            const serverAreaTakeoutData = {
                // 內用外帶專用桌號資料
                userTableData: takeOutTableData,
                dineInTable: rootGetters['spreadsheet/dineInTables'].find(el => el.areaId === takeOutTableData.areaId)
            }

            let deliverDate = rootState.cart.payment.pickupDate
            let deliverTime = rootState.cart.payment.pickupTime
            const isRetailPickUp = rootState.cart.payment.consumptionPattern === 4
            const isRetailDelivery = rootState.cart.payment.consumptionPattern === 5
            const deliveryPeriod = rootState.cart.payment.deliveryPeriod || null
            // deliverTime 格式為 HH:mm

            let deliverDateTime = ''
            if (deliverDate && (deliverTime || isRetailPickUp || isRetailDelivery)) {
                // 零售訂單只設定日期，以當日 00:00 表示所選日期
                const deliverTimeText = (isRetailPickUp || isRetailDelivery) ? '00:00' : deliverTime
                deliverDateTime = deliverDate.replaceAll('/', '-') + 'T' + deliverTimeText + ':00'
                deliverDateTime = commonUtils.convertLocalDateTimeToUTC(deliverDateTime)
            } else {
                // 如果沒有選擇取餐時間，則預設為現在時間
                // 內用 外帶時屬於用餐時間
                const current = new Date()
                // 如果是外帶且 app 有設定外帶等候時間，則需要加上等候時間
                if (rootState.cart.payment.consumptionPattern === 1 && rootGetters['basic/toGoDelayMinute'] !== 0) {
                    const delayMinute = rootGetters['basic/toGoDelayMinute']
                    current.setMinutes(current.getMinutes() + delayMinute)
                }
                deliverDateTime = commonUtils.convertLocalDateTimeToUTC(current)
            }
            const orderSource = 2

            const orderDeliveringData = {
                // 用餐模式
                consumptionPattern: rootState.cart.payment.consumptionPattern,
                // 下單來源
                // 1: pos 下單 2: 線上點餐 3:線上零售 4:無人機點餐 5:掃碼點餐
                orderSource,
                // 內用大人數量 目前沒有選擇人數介面 先預設1
                customerAdultCount: 1,
                // 兒童數量（目前未使用）
                customerChildrenCount: null,
                // 用餐區域資料
                serverAreaData,
                serverAreaTakeoutData,
                // 配送資訊（外送/自取時使用）
                address: rootState.cart.payment.address || null,
                recipient: rootState.cart.payment.recipientName || null,
                contactPhone: rootState.cart.payment.phone || null,  // 聯絡電話
                // 用餐 / 送餐 / 取餐 / 送達 / 取貨時間
                // 根據 mode 有不同意義
                deliverTime: deliverDateTime ? deliverDateTime : null,
                // 零售宅配送達時段：上午 / 下午
                deliveryPeriod
            }
            const orderDelivering = new OrderDelivering(orderDeliveringData)
            const orderSpecs = state.orderSpecs.map(item => new OrderSpec(item))

            const storeServiceFeeSetting = {
                serviceFeeMode: rootGetters['basic/serviceFeeMode'],
                serviceFeeRate: rootGetters['basic/serviceFeeRate']
            }

            const orderCheckout = new OrderCheckout({
                store_code: rootState.userData.storeData.store_code,
                payment: rootState.cart.payment,
                store_member_uuid: rootState.userData.memberInfo.store_member_uuid,
                vipLevel: rootGetters['userData/vipLevel'],
                storeServiceFeeSetting
            })


            const orderContent = {
                id: commonUtils.generateId(),
                orderGoods,
                orderSpecs: orderSpecs,
                // 訂單編號由 server 產生
                serial: '',
                orderCheckout,
                orderDelivering
            }

            // order_amount 計算
            // payment.paymentPrice 是 訂單總價 + 服務費（cart getter 已扣除紅利積分折抵）
            // 故此處不可再扣一次點數，否則會重複扣點
            // TODO: 優惠卷開通後才能使用
            const finalPrice = rootState.cart.payment.paymentPrice

            const sendData = {
                // 建立訂單不需填 order_checkout_time
                order_checkout_time: null,
                consumption_pattern: rootState.cart.payment.consumptionPattern,
                payment_method: rootState.cart.payment.paymentType,
                order_amount: finalPrice < 0 ? 0 : finalPrice,
                order_content: JSON.stringify(orderContent),
                record_uuid: commonUtils.generateUUID(),
                // 訂單來源 線上點餐:2 線上零售:3
                order_source: orderSource,
                // TODO: 取餐/取貨時間
                // take_order_time: null
                // user_data_changed 用戶資料是否有變更
                user_data_changed: rootState.cart.payment.userDataChanged,
                // user_nickname 用戶暱稱
                user_nickname: rootState.cart.payment.userName,
                // user_phone 用戶手機
                user_phone: rootState.cart.payment.userMobile,
                // 取餐 / 取貨時間
                take_order_time: deliverDateTime ? deliverDateTime : null,
            }

            const paymentType = rootState.cart.payment.paymentType
            const isLinePay = paymentType === 'linePay'

            if (isLinePay) {
                // 是否使用線上支付
                sendData.use_online_payment = 1
                sendData.payment_method_type = 1
                sendData.order_checkout_time = moment().utc().format()
            }

            // 雲端列印開啟且有印表機序號才組列印參數
            const printEnabled = rootGetters['basic/onlinePrintEnabled']
            const printerSerial = rootGetters['basic/summiPrinterSerial']
            const printWidth = rootGetters['basic/printWidth']
            const canPrint = printEnabled && !!printerSerial

            commit('setLinePayPrintFields', {})

            const res = await createOnlineOrder(sendData)
            if (res?.data?.data?.msg === 'Failed') {
                throw res?.data.data.err_msg || '建立訂單失敗'
            }
            const orderNumber = res?.data?.data?.order_number
            commit('setOrderNumber', orderNumber)

            // 現金：訂單成立後以 order_number 組列印資料，改打 direct_print 發送列印指令
            if (canPrint && !isLinePay) {
                try {
                    const printData = buildOrderDetailPrintData({
                        rootState, rootGetters, paymentType: 'cash', orderNumber, printWidth,
                    })
                    const printFields = buildPrintFields(printData, printerSerial)
                    if (printFields.print_data && printFields.printer_serial) {
                        await directPrint(printFields)
                    }
                } catch (e) {
                    console.error('cash direct_print failed', e)
                }
            }

            // LINE Pay：確認付款才印 → 此時已有 order_number，組「已結帳」版存 state
            if (canPrint && isLinePay) {
                try {
                    const printData = buildOrderDetailPrintData({
                        rootState, rootGetters, paymentType: 'linePay', orderNumber, printWidth,
                    })
                    commit('setLinePayPrintFields', buildPrintFields(printData, printerSerial))
                } catch (e) {
                    console.error('build linePay print_data failed', e)
                }
            }
            console.log('create online order res', res)
        },
        async cancelOnlineOrder({ state }, orderNumber) {
            const sendData = {
                order_number: orderNumber ? orderNumber : state.orderNumber
            }
            const res = await cancelOnlineOrder(sendData)
            if (res?.data?.data?.msg === 'Failed') {
                throw res?.data?.data?.err_msg
            }
        },
        async getOnlineOrders({ state, }, orderNumbers) {
            console.log('orderNumbers', orderNumbers, state.orderNumber)
            // 傳入 orderNumbers 為陣列
            const sendData = {
                order_number: orderNumbers
            }
            return await getOnlineOrderInfo(sendData)
        }
    }
}
